import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react'

/**
 * The r5 motion gate (PAGES.md 1.3). `html.r5-motion` is added by the inline script in index.html only when JS runs,
 * IntersectionObserver exists and the visitor has not asked for reduced motion. All r5 keyframes and transitions are
 * written under `html.r5-motion .r5 ...`, so without the class every element is already in its final state.
 */
export const R5_MOTION_CLASS = 'r5-motion'

/** True when r5 motion may run right now. Reads the gate class, so it is also false in tests and under reduced motion. */
export function motionOn(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains(R5_MOTION_CLASS)
}

/** Called once from main.tsx: tells the inline gate script that the app booted, so it keeps the class past 2.5 s. */
export function confirmR5Motion() {
  ;(window as unknown as { __r5m?: boolean }).__r5m = true
}

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/** sessionStorage key for a played band move: `r5-seen:<surface>:<id>`. */
export function seenKey(surface: string, id: string | number) {
  return `r5-seen:${surface}:${id}`
}

/** What `usePlayOnce` returns: whether the move may play, and a callback the component calls once the move has started. */
export interface PlayMove {
  readonly play: boolean
  /** Records the id as seen in this session. `useMove` calls it when the move actually starts, never at mount. */
  readonly seen: () => void
}
/** The `play` prop of every component with a band move: a plain boolean, or the `usePlayOnce` result. */
export type PlayProp = boolean | PlayMove

function wasNotSeen(key: string | null): boolean {
  if (key === null || !motionOn()) return false
  try {
    return window.sessionStorage.getItem(key) === null
  } catch {
    return true
  }
}

/**
 * Whether a surface may play a band move for a given id (PAGES.md 1.3 rule 4: band moves play once per result per session).
 * Pass an id that changes on a real event, for example `${resultId}:${state}`, so a result that goes from Being marked to a
 * band plays again. Hand the result to a component's `play` prop: the component starts the move when it scrolls into view
 * (`useMove`) and only then records the id, so a chart below the fold is not used up unseen.
 *
 * Reads storage when the id first appears (and again when it changes); a re-render never flips a playing move off, and a
 * remount in the same session after the move started shows the final state. Storage can throw (private window): then it
 * plays once per mount.
 */
export function usePlayOnce(surface: string, id: string | number | null | undefined): PlayMove {
  const key = id === null || id === undefined ? null : seenKey(surface, id)
  const [state, setState] = useState(() => ({ key, play: wasNotSeen(key) }))
  let play = state.play
  if (state.key !== key) {
    play = wasNotSeen(key)
    setState({ key, play })
  }
  const seen = useCallback(() => {
    if (key === null) return
    try { window.sessionStorage.setItem(key, '1') } catch { /* a private window plays the move once per mount */ }
  }, [key])
  return useMemo(() => ({ play, seen }), [play, seen])
}

/**
 * The gate every band move goes through. Returns true when `play` allows the move AND the element is in view (above the
 * fold at mount counts as in view), which is what the component writes to `data-play`; calls `seen()` the first time that
 * becomes true. `watch` re-attaches the observer when the element mounts late (a chart that has no data at first).
 */
export function useMove(play: PlayProp | undefined, ref: RefObject<HTMLElement | null>, watch?: unknown): boolean {
  const { shown } = useReveal(ref, { watch })
  const want = typeof play === 'object' ? play.play : Boolean(play)
  const go = want && shown
  const seen = typeof play === 'object' ? play.seen : undefined
  useEffect(() => {
    if (go) seen?.()
  }, [go, seen])
  return go
}

interface RevealOptions {
  /** Start the move when the element's top is within this share of the viewport bottom (r5: 10%). */
  margin?: number
  /** Re-attach the observer when this changes (the element mounted late). */
  watch?: unknown
}

/**
 * Drives `Reveal` and `Stagger`: adds `.r5-in` once, when the element scrolls into view. An element already in view at
 * mount is never parked (it gets `.r5-in` and `data-now` before paint). Without the motion gate nothing is parked, so
 * the class is applied at once and no observer is created.
 */
export function useReveal<T extends HTMLElement>(ref: RefObject<T | null>, { margin = 0.1, watch }: RevealOptions = {}) {
  const [shown, setShown] = useState(false)
  const [now, setNow] = useState(false)
  const observer = useRef<IntersectionObserver | null>(null)

  useIsomorphicLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    if (!motionOn() || typeof IntersectionObserver === 'undefined') {
      setShown(true)
      setNow(true)
      return
    }
    const rect = element.getBoundingClientRect()
    if (rect.top < window.innerHeight * (1 - margin)) {
      setShown(true)
      setNow(true)
      return
    }
    observer.current = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShown(true)
        observer.current?.disconnect()
      }
    }, { rootMargin: `0px 0px -${Math.round(margin * 100)}% 0px`, threshold: 0 })
    observer.current.observe(element)
    return () => observer.current?.disconnect()
  }, [ref, margin, watch])

  return { shown, now }
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* The viewer's Motion setting (APP-DESIGN-DIRECTION 3.3). Stored per viewer, read by the gate script in index.html.     */
/* ------------------------------------------------------------------------------------------------------------------ */

export type MotionChoice = 'device' | 'reduced' | 'full'
export const MOTION_KEY = 'r5-motion'
export const CELEBRATIONS_KEY = 'r5-celebrations'
const FULL_CLASS = 'r5-motion-full'

export function readMotionChoice(): MotionChoice {
  try {
    const value = window.localStorage.getItem(MOTION_KEY)
    return value === 'reduced' || value === 'full' ? value : 'device'
  } catch {
    return 'device'
  }
}

function deviceReduced(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return true
  }
}

/** Whether a given choice animates on this device (the same rule the gate script applies at load). */
export function choiceAnimates(choice: MotionChoice, reduced = deviceReduced()): boolean {
  if (choice === 'reduced') return false
  if (choice === 'full') return true
  return !reduced
}

/**
 * Saves the choice and applies it at once: the gate class and the "full" opt-out are set on <html> without a reload.
 * Storage can throw (private window): the choice then holds for this page view only.
 */
export function setMotionChoice(choice: MotionChoice) {
  try {
    if (choice === 'device') window.localStorage.removeItem(MOTION_KEY)
    else window.localStorage.setItem(MOTION_KEY, choice)
  } catch { /* this view only */ }
  const root = document.documentElement
  const on = choiceAnimates(choice) && typeof IntersectionObserver !== 'undefined'
  root.classList.toggle(R5_MOTION_CLASS, on)
  root.classList.toggle(FULL_CLASS, on && choice === 'full')
}

/** Celebrations (confetti) have their own switch under Motion; default on. */
export function celebrationsOn(): boolean {
  try {
    return window.localStorage.getItem(CELEBRATIONS_KEY) !== 'off'
  } catch {
    return true
  }
}

export function setCelebrations(on: boolean) {
  try {
    if (on) window.localStorage.removeItem(CELEBRATIONS_KEY)
    else window.localStorage.setItem(CELEBRATIONS_KEY, 'off')
  } catch { /* this view only */ }
}
